/**
 * Utility helpers to handle UTC timestamps for Supabase & TracQ.
 *
 * Supabase stores timestamps in UTC. These functions help convert between
 * local time (used in input/display) and UTC (used for storage/comparison).
 */

/**
 * Converts a local Date to an ISO string in UTC.
 *
 * @param date - Local date to convert
 *
 * @returns ISO string in UTC format
 *
 */
export function toUTC(date: Date): string {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString();
}

/**
 * Converts a UTC ISO string from Supabase to a local Date.
 *
 * @param utcDate - UTC ISO string to convert
 *
 * @returns Local Date object
 *
 */
export function fromUTC(utcDate: string): Date {
  return new Date(utcDate); // JS Date handles this correctly
}

/**
 * Converts specific fields of a Supabase row from UTC ISO strings to local
 * Date objects. Useful to automatically transform query results.
 *
 * @param rows - Array of rows to transform
 *
 * @param fields - Array of field names to convert
 *
 * @returns Array of rows with converted date fields
 *
 */
export function mapUtcFieldsToLocal<T extends Record<string, unknown>>(
  rows: T[],
  fields: (keyof T)[],
): T[] {
  return rows.map((row) => {
    const result = { ...row };
    for (const key of fields) {
      const value = row[key];
      if (typeof value === "string") {
        (result as Record<string, unknown>)[key as string] = new Date(value);
      }
    }
    return result;
  });
}

const DEFAULT_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  dateStyle: "medium",
  timeStyle: "short",
};

/**
 * Formats a UTC ISO string as a localized string (e.g. for display).
 * Automatically uses user's local time zone.
 *
 * @param utcString - UTC ISO string to format
 *
 * @param options - Intl.DateTimeFormatOptions (defaults to medium date, short time)
 *
 * @param locale - Locale string (defaults to "de-DE")
 *
 * @returns Formatted date string
 *
 */
export function formatUTC(
  utcString: string,
  options?: Intl.DateTimeFormatOptions,
  locale = "de-DE",
): string {
  try {
    const date = fromUTC(utcString);
    return date.toLocaleString(locale, {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      ...DEFAULT_FORMAT_OPTIONS,
      ...options,
    });
  } catch {
    return utcString;
  }
}

/**
 * Converts a datetime-local string (e.g. from <input type="datetime-local">)
 * to a UTC ISO string for storage in Supabase.
 *
 * Example:
 *   "2025-06-16T15:12" → "2025-06-16T13:12:00.000Z" (in UTC+2)
 *
 * @param input - Datetime-local string to convert
 *
 * @returns UTC ISO string
 *
 */
export function parseInputToUTC(input: string): string {
  try {
    const localDate = new Date(input);
    return toUTC(localDate);
  } catch {
    return input;
  }
}
